<template>
    <div class="row">
        <div class="col-sm-12" v-if="employerLinks.length">
            <h2>Employer Links</h2>
            <div class="alert alert-info">
                New Employers in the payment list. Assign the employers first and refresh the page when you're done.
                The site may be able to solve some orphaned transactions with the new info.
            </div>

            <table class="table table-responsive table-striped table-hover">
                <thead>
                <tr>
                    <th>Customer/Email</th>
                    <th>Order</th>
                    <th>Link to</th>
                    <th></th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="(e, index) in employerLinks">
                    <td>{{ e.customer_name || e.customer_email }}<br>
                    <span class="label" :class="{'label-primary': e.obj_type === 'btree', 'label-danger': e.obj_type === 'stripe'}">{{ e.obj_type }}</span></td>
                    <td>{{ e.description }}</td>
                    <td v-if="showSelectEmployerLinks[index] !== true" colspan="2">
                        <el-button type="primary" size="small" @click="triggerEmployers(index)" :disabled="isAssignEmployerLinkBusy !== false">
                            <template v-if="isAssignEmployerLinkBusy === e.customer_id">
                                <i class="fa fa-spinner fa-pulse"></i>
                            </template>
                            <template v-else>
                                Select Employer
                            </template>
                        </el-button>
                    </td>
                    <template v-else>
                        <td>
                            <el-select v-model="e.employer_id"
                                       :disabled="isAssignEmployerLinkBusy !== false"
                                       filterable
                                       @visible-change="hideEmployerLink($event, index)"
                                       placeholder="Select Employer">
                                <el-option
                                        v-for="(el) in employers"
                                        :key="el.id"
                                        :label="`${el.name}` + (el.id > 0 ? ` (${el.id})` : ``)"
                                        :value="el.id">
                                </el-option>
                            </el-select>
                        </td>
                        <td>
                            <el-button type="warning" @click="assignEmployer(e, index)" :disabled="isAssignEmployerLinkBusy !== false">
                                <template v-if="isAssignEmployerLinkBusy === e.customer_id">
                                    <i class="fa fa-spinner fa-pulse"></i>
                                </template>
                                <template v-else>
                                    Assign
                                </template>
                            </el-button>
                        </td>
                    </template>
                </tr>
                </tbody>
            </table>
        </div>

        <div class="col-sm-12" v-if="transactionLinks.length">
            <h2>Orphaned Transactions</h2>
            <table class="table table-responsive table-striped table-hover">
                <thead>
                <tr>
                    <th>Trans ID, Date</th>
                    <th>Amount</th>
                    <th>Customer</th>
                    <th>Order</th>
                    <th>Employer</th>
                    <th>Period</th>
                    <th></th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="(e, index) in transactionLinks">
                    <td>{{ e.payment_id }}<br>{{ e.created_date }}</td>
                    <td>{{ e.amount }}<br><span class="label" :class="{'label-primary': e.obj_type === 'btree', 'label-danger': e.obj_type === 'stripe'}">
                        {{ e.obj_type }}
                    </span></td>
                    <td>{{ e.customer_name }}<br>{{ e.customer_email }}</td>
                    <td>{{ e.description }}<br>
                        {{ e.type }}, {{ e.status }}</td>
                    <template v-if="showSelectOrphanedTransactions[index] !== true && (showSelectOrphanedTransactions[index] !== 'loadingBudgets' || employers.length === 0)">
                        <template v-if="!!e.employer_id">
                            <td colspan="3">
                                <span>{{ e.employer_name }} ({{ e.employer_id }})</span><br>
                                <el-button type="info" size="mini" @click="triggerBudgets(e.employer_id, index)" :disabled="loadingEmployers">
                                    <template v-if="showSelectOrphanedTransactions[index] === 'loadingBudgets'">
                                        <i class="fa fa-spinner fa-pulse"></i>
                                    </template>
                                    <template v-else>
                                        Select Period
                                    </template>
                                </el-button>
                            </td>
                        </template>
                        <template v-else>
                            <td colspan="3">
                                <el-button type="primary" size="mini" @click="triggerBudgets(null, index)" :disabled="loadingEmployers">
                                    <template v-if="showSelectOrphanedTransactions[index] === 'loadingEmployers'">
                                        <i class="fa fa-spinner fa-pulse"></i>
                                    </template>
                                    <template v-else>
                                        Select Employer
                                    </template>
                                </el-button>
                            </td>
                        </template>
                    </template>
                    <template v-else>
                        <td>
                            <el-select v-model="e.employer_id"
                                       filterable
                                       placeholder="Select Employer"
                                       @input="triggerBudgets(e.employer_id, index)">
                                <el-option v-for="(el) in employers"
                                           :key="el.id"
                                           :label="`${el.name}` + (el.id > 0 ? ` (${el.id})` : ``)"
                                           :value="el.id.toString()">
                                </el-option>
                            </el-select>
                        </td>
                        <td>
                            <el-select v-model="e.budget_id"
                                       filterable
                                       placeholder="Select Period"
                                       v-if="budgetPeriods[e.employer_id]">
                                <el-option
                                        v-for="(el) in budgetPeriods[e.employer_id]"
                                        :key="el.id"
                                        :label="el.name"
                                        :value="el.id.toString()">
                                </el-option>
                            </el-select>
                            <i v-if="showSelectOrphanedTransactions[index] === 'loadingBudgets'" class="fa fa-spinner fa-pulse"></i>
                        </td>
                        <td>
                            <el-button type="warning" size="medium" @click="assignBudgetEmployer(e, index)" :disabled="loadingEmployers || isAssignPaymentLinkBusy !== false">
                                <template v-if="isAssignPaymentLinkBusy !== false && isAssignPaymentLinkBusy === e.payment_id">
                                    <i class="fa fa-spinner fa-pulse"></i>
                                </template>
                                <template v-else>
                                    Assign
                                </template>
                            </el-button>
                        </td>
                    </template>
                </tr>
                </tbody>
            </table>
        </div>

        <div class="col-sm-12" v-if="discardedTransactions.length">
            <h2>Discarded Transactions</h2>
            <table class="table table-responsive table-striped table-hover">
                <thead>
                <tr>
                    <th>Trans ID, Date</th>
                    <th>Amount</th>
                    <th>Customer</th>
                    <th>Order</th>
                    <th></th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="e in discardedTransactions">

                    <td>{{ e.payment_id }}<br>{{ e.created_date }}</td>
                    <td>{{ e.amount }}<br><span class="label" :class="{'label-primary': e.obj_type === 'btree', 'label-danger': e.obj_type === 'stripe'}">
                        {{ e.obj_type }}
                    </span></td>
                    <td>{{ e.customer_name }}<br>{{ e.customer_email }}</td>
                    <td>{{ e.description }}<br>{{ e.type }}, {{ e.status }}</td>
                    <td>
                        <el-button type="danger" size="small" @click="confirmDiscarded(e)" :disabled="confirmingDiscarded !== null">
                            <template v-if="confirmingDiscarded !== null && confirmingDiscarded === e.obj_type + '-' + e.id">
                                <i class="fa fa-spinner fa-pulse"></i>
                            </template>
                            <template v-else>Confirm</template>
                        </el-button>
                    </td>
                </tr>
                </tbody>
            </table>
        </div>

        <div class="col-sm-12" v-if="(discardedTransactions.length + transactionLinks.length + employerLinks.length) === 0">
            <h2>No Orphaned Transactions</h2>
        </div>
    </div>
</template>
<script>
    export default {
        props: ['data', 'root_url'],
        data() {
            return {
                employerLinks: _.cloneDeep(this.data.employer_links),
                transactionLinks: _.cloneDeep(this.data.transaction_links),
                discardedTransactions: _.cloneDeep(this.data.discarded_transactions),
                emp_id: 0,
                showSelectEmployerLinks: [],
                isAssignEmployerLinkBusy: false,
                showSelectOrphanedTransactions: [],
                confirmingDiscarded: null,
                employers: [],
                loadingEmployers: false,
                isAssignPaymentLinkBusy: false,
                budgetPeriods: [],
            };
        },
        methods: {
            triggerEmployers(employerLinkIndex) {
                if (this.employers.length === 0) {
                    this.isAssignEmployerLinkBusy = this.employerLinks[employerLinkIndex].customer_id;
                    this.getEmployers(null, employerLinkIndex, null);
                } else {
                    Vue.set(this.showSelectEmployerLinks, employerLinkIndex, true);
                }
            },
            triggerBudgets(employerId, orphanPaymentIndex) {
                if (this.employers.length === 0) {
                    Vue.set(this.showSelectOrphanedTransactions, orphanPaymentIndex, 'loadingEmployers');
                    this.loadingEmployers = true;
                    this.getEmployers(employerId, null, orphanPaymentIndex);
                }

                if (!!employerId && !this.budgetPeriods[employerId]) {
                    Vue.set(this.showSelectOrphanedTransactions, orphanPaymentIndex, 'loadingBudgets');
                    this.getBudgetPeriods(employerId, orphanPaymentIndex);
                }

                if (this.employers.length > 0 &&
                    (
                        (this.showSelectOrphanedTransactions[orphanPaymentIndex] !== false &&
                            this.showSelectOrphanedTransactions[orphanPaymentIndex] !== 'loadingBudgets') ||
                        this.budgetPeriods[employerId]
                    )
                ) {
                    Vue.set(this.showSelectOrphanedTransactions, orphanPaymentIndex, true);
                }
            },
            getEmployers(employerId, employerLinkIndex, orphanPaymentIndex) {
                const self = this;
                axios.get(`${self.root_url}/get-employers`)
                     .then(response => {
                         self.employers = response.data.employers;
                     })
                     .finally(() => {
                         self.loadingFinished(employerId, employerLinkIndex, orphanPaymentIndex);
                     });
            },
            getBudgetPeriods(employerId, orphanPaymentIndex) {
                const self = this;
                axios.post(`${self.root_url}/get-billing-periods`, { employerId: employerId })
                     .then((response) => {
                         Vue.set(self.budgetPeriods, employerId, response.data);
                     })
                     .finally(() => {
                         self.loadingFinished(employerId, null, orphanPaymentIndex);
                     });
            },
            loadingFinished(employerId, employerLinkIndex, orphanPaymentIndex) {
                if (orphanPaymentIndex !== null && this.employers.length > 0 && (!employerId || this.budgetPeriods[employerId])) {
                    Vue.set(this.showSelectOrphanedTransactions, orphanPaymentIndex, true);
                    this.loadingEmployers = false;
                }

                if (employerLinkIndex !== null && this.employers.length > 0) {
                    Vue.set(this.showSelectEmployerLinks, employerLinkIndex, true);
                    this.isAssignEmployerLinkBusy = false;
                }
            },
            hideEmployerLink(event, index) {
                if (event === false && !this.employerLinks[index].employer_id) {
                    Vue.set(this.showSelectEmployerLinks, index, false);
                }
            },
            assignEmployer(e, index) {
                const self = this;
                self.isAssignEmployerLinkBusy = e.customer_id;

                axios.post(`${self.root_url}/${e.obj_type}-assign-employer`, {
                    customer_id: e.customer_id,
                    customer_email: e.customer_email,
                    customer_name: e.customer_name,
                    employer_id: e.employer_id,
                }).then(response => {
                    self.employerLinks = _.without(self.employerLinks, e);
                    self.$set(self.showSelectEmployerLinks, index, false);
                }).finally(() => {
                    self.isAssignEmployerLinkBusy = false;
                });
            },
            assignBudgetEmployer(e, index) {
                const self = this;
                self.isAssignPaymentLinkBusy = e.payment_id;
                axios.post(`${this.root_url}/${e.obj_type}-assign-billing-period`, {
                    id: e.id,
                    employer_id: e.employer_id,
                    budget_period_id: e.budget_id,
                }).then(response => {
                    Vue.set(this.showSelectOrphanedTransactions, index, false);
                    if (e.budget_id) {
                        self.transactionLinks = _.without(self.transactionLinks, e);
                    } else {
                        e.employer_name = _.find(this.employers, function(o) { return o.id == e.employer_id; }).name;
                        Vue.set(self.transactionLinks, index, e);
                    }
                }).finally(() => {
                    self.isAssignPaymentLinkBusy = false;
                });
            },
            confirmDiscarded(e) {
                const self = this;
                self.confirmingDiscarded = e.obj_type + '-' + e.id;
                axios.post(`${self.root_url}/${e.obj_type}-confirm-discarded`, { id: e.id })
                     .then(response => {
                         self.discardedTransactions = _.without(self.discardedTransactions, e);
                     })
                     .finally(() => {
                         self.confirmingDiscarded = null;
                     });
            },
        }
    }
</script>
